import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { Button, Flex, Link, Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';
import Banner from '@rover/kibble/official-patterns/Banner';
import Toast, { useToast } from '@rover/kibble/patterns/Toast';
import {
  catCareDetails,
  NavigationSource,
} from '@rover/react-lib/src/pages/provider-profile/constants/routes';
import { ReturnQueryParam } from '@rover/react-lib/src/pages/provider-profile/types/ServiceForm';
import type { RetrieveCatProviderProfile } from '@rover/rsdk/src/apiClient/latest/model/retrieveCatProviderProfile';

export type SkillsFormData = {
  dogSkills: string[];
  catSkills: string[];
};

export type Props = {
  initialData: SkillsFormData;
  isSubmitting: boolean;
  onSubmit: (data: SkillsFormData) => void;
  setShowBackModal: (isDirty: boolean) => void;
  submissionErrors?: Record<string, string[] | string>;
  offersDogCare: boolean;
  offersCatCare: boolean;
  catProfile?: RetrieveCatProviderProfile | null;
};

const SkillsForm = ({
  initialData,
  isSubmitting,
  onSubmit,
  setShowBackModal,
  submissionErrors = {},
  offersDogCare,
  offersCatCare,
  catProfile,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toastProperties, isToastVisible, toastKey, showToast } = useToast();

  const { configureField, formProps, formState, setError } = useKibbleForm({
    schema: z.object({
      dogSkills: z.array(z.string()),
      catSkills: z.array(z.string()),
    }),
    defaultValues: initialData,
    onValidSubmit: onSubmit,
  });

  const navigateToCatCareDetailsPage = (): void => {
    const params = new URLSearchParams();
    params.append('source', NavigationSource.Skills);
    const destinationUrl = `${catCareDetails}?${params.toString()}`;

    navigate(destinationUrl);
  };

  // notify back button in parent component
  useEffect(() => {
    setShowBackModal(formState.isDirty);
  }, [setShowBackModal, formState]);

  // apply submission errors from API to form fields
  useEffect(() => {
    Object.entries(submissionErrors).forEach(([fieldName, errors]) => {
      if (fieldName in { dogSkills: true, catSkills: true }) {
        const errorMessage = Array.isArray(errors) ? errors.join(' ') : errors;
        setError(fieldName as keyof SkillsFormData, {
          type: 'server',
          message: errorMessage,
        });
      }
    });
  }, [submissionErrors, setError]);

  useEffect(() => {
    const { search, pathname } = location;
    const catExperienceAdded = new URLSearchParams(search).get(ReturnQueryParam.CatExperienceAdded);

    if (catExperienceAdded && catProfile) {
      // Show toast only when redirected from cat care details page
      showToast({
        message: t`Added cat experience to your sitter profile!`,
        severity: 'success',
      });
    }

    if (catExperienceAdded) {
      // Clean up URL by removing catExperienceAdded param
      navigate({ pathname, search: '' }, { replace: true });
    }
  }, []);

  const dogSkillsOptions = [
    {
      value: 'first_aid_cpr',
      primaryLabel: t`First aid and/or CPR`,
    },
    {
      value: 'can_provide_oral_medication',
      primaryLabel: t`Oral medication administration`,
    },
    {
      value: 'can_provide_injected_medication',
      primaryLabel: t`Injectable medication administration`,
    },
    {
      value: 'senior_dog_care_experience',
      primaryLabel: t`Senior care`,
    },
    {
      value: 'special_needs_dog_care_experience',
      primaryLabel: t`Special needs care`,
    },
    {
      value: 'can_provide_daily_exercise',
      primaryLabel: t`Daily exercise for high-energy dogs`,
    },
  ];

  const catSkillsOptions = [
    {
      value: 'can_provide_oral_medication',
      primaryLabel: t`Oral medication administration`,
    },
    {
      value: 'can_provide_injected_medication',
      primaryLabel: t`Injectable medication administration`,
    },
  ];

  return (
    <Form {...formProps} hideRequiredFieldWarning>
      {(offersDogCare || (offersCatCare && catProfile)) && (
        <Text textColor="primary" size="200" mb="6x">
          {t`Can you provide any of the following?`}
        </Text>
      )}

      {offersDogCare && (
        <CheckboxField
          {...configureField('dogSkills')}
          primaryLabel={t`Dog skills`}
          required={false}
          hideRequiredAsterisk
          options={dogSkillsOptions}
        />
      )}
      {offersCatCare && catProfile && (
        <CheckboxField
          {...configureField('catSkills')}
          primaryLabel={t`Cat skills`}
          required={false}
          hideRequiredAsterisk
          options={catSkillsOptions}
        />
      )}

      {offersCatCare && !catProfile && (
        <Banner severity="info" containerProps={{ mt: '2x' }} hideIcon>
          <Flex flexDirection="column" gap="1x">
            <Text size="100" fontWeight="semibold">{t`New: Cat skills`}</Text>
            <Text size="100">
              <Trans>
                We’ve added new ways to highlight your qualifications to cat parents. To access
                these new skills, start by{' '}
                <Link href="#" onPress={navigateToCatCareDetailsPage}>
                  adding Cat experience
                </Link>{' '}
                to your Sitter Profile.
              </Trans>
            </Text>
          </Flex>
        </Banner>
      )}

      {isToastVisible && (
        <Toast
          key={toastKey}
          {...toastProperties}
          containerProps={{
            zIndex: '9999',
          }}
        />
      )}

      <Button
        webProps={{ type: 'submit' }}
        disabled={isSubmitting}
        loading={isSubmitting}
        variant="primary"
        mt="12x"
        fullWidth
      >
        {t`Save & Continue`}
      </Button>
    </Form>
  );
};

export default SkillsForm;
